import { ProgramFilterStatusBackEndKey } from 'utils/enums';

// Simple helpers (replace with dayjs if you prefer)
const toDate = iso => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return null;
  }
};

export const toCurrency = n =>
  typeof n === 'number'
    ? n.toLocaleString('el-GR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      })
    : undefined;

// Code ➜ label lookups
export const SIZE_MAP = {
  MICRO: 'Πολύ Μικρή',
  SMALL: 'Μικρή',
  MEDIUM: 'Μεσαία',
  LARGE: 'Μεγάλη',
};

export const EXPENSE_MAP = {
  VEH_USE: 'Χρήση οχημάτων',
  LAND_USE: 'Χρήση γης/ακινήτων',
  CONS: 'Καταναλωτικά/Υλικά',
  INT_PUR: 'Αγορές άυλων/δικαιωμάτων',
};

export const LEGAL_FORM_CODES_MAP = {
  SP: "ΑΤΟΜΙΚΗ",
  GP: "ΟΜΟΡΡΥΘΜΗ ΕΤΑΙΡΕΙΑ",
  LP: "ΕΤΕΡΟΡΡΥΘΜΗ ΕΤΑΙΡΕΙΑ",
  LLC: "ΕΤΑΙΡΕΙΑ ΠΕΡΙΟΡΙΣΜΕΝΗΣ ΕΥΘΥΝΗΣ",
  PC: "ΙΔΙΩΤΙΚΗ ΚΕΦΑΛΑΙΟΥΧΙΚΗ ΕΤΑΙΡΕΙΑ",
  PLC: "ΑΝΩΝΥΜΗ ΕΤΑΙΡΕΙΑ",
  SC: "ΚΟΙΝΩΝΙΚΗ ΣΥΝΕΤΑΙΡΙΣΤΙΚΗ ΕΠΙΧΕΙΡΗΣΗ",
  OTHER: "ΑΛΛΟ",
};

export const COMPANY_TYPE_MAP = {
  EXISTING: 'Υφιστάμενη',
  NEW: 'Νέα',
  UNDER_FORMATION: 'Υπό Σύσταση',
};

export const SUBSIBY_TYPE_MAP = {
  GRANT_SUBSIDY: 'Επιχορήγηση/Επιδότηση',
  WORKING_CAPITAL_GROWTH: 'Κατάρτιση προσωπικού',
  RDI_SUPPORT: 'Κεφάλαιο κίνησης για ανάπτυξη επιχείρησης',
  LOAN_INTEREST_SUBSIDY: 'Δάνειο/Επιδότηση επιτοκίου',
  LOAN_GUARANTEE: 'Εγγύηση δανείου',
};

export const LAW_CODE_MAP = {
  GAK: "ΓΑΚ",
  DEMINIMIS: "De Minimis",
};

export const EXPENSE_CLASS_CODE_MAP = { 
  ANALOSIMA: "Αναλώσιμα",
  AULA: "Άυλα αγορά, Άυλα χρήση (αποσβέσεις/συνδρομές)",
  GIPEDA_AGORA: "Γήπεδα αγορά",
  GIPEDA_XRISI: "Γήπεδα χρήση (αποσβέσεις/μισθώσεις), Διαμόρφωση γηπέδων",
  MARKETING: "Δαπάνες προώθησης και επικοινωνίας (Marketing)",
  EXOPLISMOS: "Εξοπλισμός αγορά/κατασκευή, Εξοπλισμός χρήση (αποσβέσεις/μισθώσεις)",
  KEFALAIO_KINISIS: "Κεφάλαιο κίνησης (δαπάνες λειτουργίας, δαπάνες σχετικές με το συναλλακτικό κύκλωμα της επιχείρησης, ΦΠΑ, κ.λπ.)",
  KOSTOS_KEFALAION: "Κόστος κεφαλαίων",
  KTHRIA_AGORA: "Κτήρια αγορά / κατασκευή, Κτήρια χρήση (αποσβέσεις/μισθώσεις)",
  LEITOURGIKA: "Λειτουργικά (επικοινωνία, ενέργεια, συντήρηση, μισθώματα, έξοδα διοίκησης, ασφάλιση κ.λπ..)",
  METAKINISEIS: "Μετακινήσεις / εξοδολόγια",
  METAFORIKA_MESA: "Μεταφορικά μέσα αγορά, Μεταφορικά μέσα χρήση (αποσβέσεις/μισθώσεις)",
  MISTHODOSIA_GAK: "Μισθοδοσία συνδεδεμένη με το επενδυτικό σχέδιο (όπως προβλέπεται από τον ΓΑΚ)",
  YPHRESIES_TRITON: "Υπηρεσίες τρίτων",
  MI_EPILEXIMES_DAPANES: "Μη επιλέξιμες δαπάνες",
};

// Build "50% – 70% (10.000€ – 150.000€)" if amounts exist
export const buildFundingPercentage = (minPct, maxPct) => {
  if (minPct == null && maxPct == null) return undefined;
  const pct =
    minPct != null && maxPct != null
      ? `${minPct} – ${maxPct}%`
      : minPct != null
      ? `${minPct}%`
      : `${maxPct}%`;

  return `${pct}`;
};

export const buildFundingAmount = (minAmt, maxAmt) => {
  const amt =
    minAmt != null && maxAmt != null
      ? `${toCurrency(minAmt)} – ${toCurrency(maxAmt)}`
      : minAmt != null
      ? ` (${toCurrency(minAmt)})`
      : maxAmt != null
      ? ` (${toCurrency(maxAmt)})`
      : '';

  return `${amt}`;
};

// ONE program item ➜ card props
export const mapProgram = api => ({
  id: api.id,
  source: api.fundingSourceCode, // "ESPA"
  title: api.title,
  description: api.description,
  closesInDays: api.remainingDays, // number
  sizes: (api.companySizeCodes || []).map(c => SIZE_MAP[c] || c),
  categories: (api.expenseClassCodeList || []).map(c => EXPENSE_MAP[c] || c),
  fundingPercentage: buildFundingPercentage(
    api.minFundingPercentage,
    api.maxFundingPercentage
  ),
  fundingAmount: buildFundingAmount(api.minFundingAmount, api.maxFundingAmount),
  deadline: toDate(api.endOfSubmission),
});

// Whole payload ➜ sections array you can render in order
export const mapPayloadToSections = payload => [
  {
    key: ProgramFilterStatusBackEndKey.ClosingSoon,
    title: 'Προγράμματα που κλείνουν σύντομα',
    programs: (payload[ProgramFilterStatusBackEndKey.ClosingSoon] || []).map(
      mapProgram
    ),
  },
  {
    key: ProgramFilterStatusBackEndKey.Open,
    title: 'Ανοιχτά προγράμματα',
    programs: (payload[ProgramFilterStatusBackEndKey.Open] || []).map(
      mapProgram
    ),
  },
  // {
  //   key: ProgramFilterStatusBackEndKey.Insights,
  //   title: 'GT Insights',
  //   programs: (payload[ProgramFilterStatusBackEndKey.Insights] || []).map(
  //     mapProgram
  //   ),
  // },
  {
    key: ProgramFilterStatusBackEndKey.Expired,
    title: 'Ληγμένα προγράμματα',
    programs: (payload[ProgramFilterStatusBackEndKey.Expired] || []).map(
      mapProgram
    ),
  },
];

/**
 * @description It actually maps with the local state keys of the reducer of useFundingFilters for categories.
 * - fundingInsights
 * - expired
 * - closeToExpire
 * - openProgram
 */
export const sectionTitleToLocalStateKeyMap = {
  'Προγράμματα που κλείνουν σύντομα': 'closeToExpire',
  'Ανοιχτά προγράμματα': 'openProgram',
  'GT Insights': 'fundingInsights',
  'Ληγμένα προγράμματα': 'expired',
};
